import { combineLatest, tap } from 'rxjs'

import { layoutRootElement$ } from '../pages/layout'
import { getHeaderStyleState, headerStyleState$ } from './style'
import {
  HeaderColorCssValue,
  HeaderColorCssVariable,
  HeaderCssValue,
  HeaderCssVariable,
  HeaderSpacingCssValue,
  HeaderSpacingCssVariable,
  HeaderStyle,
  HeaderStyleState,
  HeaderTextCssVariable,
  HeaderTransitionCssValue,
  HeaderTransitionCssVariable,
} from './types'

const textCssVariables = {
  '--header-text-color': HeaderColorCssValue.BRAND_NAVY,
} satisfies Record<HeaderTextCssVariable, HeaderColorCssValue>

const colorCssVariables = {
  transparent: {
    '--header-background-color': HeaderColorCssValue.TRANSPARENT,
    '--header-border-color': HeaderColorCssValue.TRANSPARENT,
    '--header-mobile-open-background-color': HeaderColorCssValue.WHITE,
    '--header-mobile-open-border-color': HeaderColorCssValue.BRAND_RULE_GREY,
  },
  lightBlue: {
    '--header-background-color': HeaderColorCssValue.BRAND_NAVY_3,
    '--header-border-color': HeaderColorCssValue.TRANSPARENT,
    '--header-mobile-open-background-color': HeaderColorCssValue.BRAND_NAVY_3,
    '--header-mobile-open-border-color': HeaderColorCssValue.TRANSPARENT,
  },
  white: {
    '--header-background-color': HeaderColorCssValue.WHITE,
    '--header-border-color': HeaderColorCssValue.BRAND_RULE_GREY,
    '--header-mobile-open-background-color': HeaderColorCssValue.WHITE,
    '--header-mobile-open-border-color': HeaderColorCssValue.BRAND_RULE_GREY,
  },
} satisfies Record<HeaderStyle, Record<HeaderColorCssVariable, HeaderColorCssValue>>

// chrome-only padding: the notification's variable height is reserved in-flow by the Header
// spacer copy (see Header.tsx), not by these values
const spacingCssVariables = {
  transparent: {
    '--header-padding-top': 'var(--hiding-header-brand-menu-height)',
  },
  solid: {
    '--header-padding-top': 'var(--hiding-header-height)',
  },
} satisfies Record<'transparent' | 'solid', Record<HeaderSpacingCssVariable, HeaderSpacingCssValue>>

/**
 * The bar cross-fades as you scroll or hover, but the search overlay mounts and unmounts with no
 * animation of its own, so while it is open the bar has to change colour in the same frame rather
 * than fade in underneath a panel that is already fully painted.
 *
 * Carried as a variable, not a Tailwind class, so it lands in the same style write as the colours
 * below: this pipeline writes synchronously when the signal fires, whereas a class arrives on
 * React's later re-render, by which point the cross-fade has already started and changing its
 * duration no longer restarts it.
 */
const transitionCssVariables = {
  instant: { '--header-transition-duration': '0s' },
  animated: { '--header-transition-duration': '300ms' },
} satisfies Record<
  'instant' | 'animated',
  Record<HeaderTransitionCssVariable, HeaderTransitionCssValue>
>

export const getHeaderStyleCssVariables = (
  state: HeaderStyleState,
): Record<HeaderCssVariable, HeaderCssValue> => ({
  ...textCssVariables,
  ...colorCssVariables[state.headerStyle],
  ...spacingCssVariables[state.headerStyleBase === 'transparent' ? 'transparent' : 'solid'],
  ...transitionCssVariables[state.isSearchOpen ? 'instant' : 'animated'],
})

export const getInitialHeaderStyleCssVariables = (headerStyleBase: HeaderStyle) =>
  getHeaderStyleCssVariables(
    getHeaderStyleState({
      isScrolled: false,
      headerStyleBase,
      isHovered: false,
      hasActiveItem: false,
      isSearchOpen: false,
    }),
  )

export const applyHeaderStyleCssVariables =
  (layoutRootElement: HTMLDivElement) => (variables: Record<HeaderCssVariable, HeaderCssValue>) => {
    Object.entries(variables).forEach(([name, value]) => {
      if (value) {
        layoutRootElement.style.setProperty(name, value ?? null)
      } else {
        layoutRootElement.style.removeProperty(name)
      }
    })
  }

// update CSS variables on Layout root element when element or style changes
export const maintainHeaderStyleCssVariables$ = combineLatest([
  layoutRootElement$,
  headerStyleState$,
]).pipe(
  tap(([layoutRootElement, headerStyleState]) => {
    if (!layoutRootElement) {
      return
    }
    const variables = getHeaderStyleCssVariables(headerStyleState)
    applyHeaderStyleCssVariables(layoutRootElement)(variables)
  }),
)
